// PartnerLogo
//
// Renders a partner's brand logo with a four-tier fallback:
//
//   1. partner.brand_logo (base64 data URL OR public URL)
//        Highest fidelity — the partner explicitly uploaded an image via
//        PartnerSettingsScreen, so we trust it without any network round
//        trip.
//
//   2. /partners/logo/{account_id} (the partners-service binary endpoint)
//        Same image as #1 in most cases (the partners service streams
//        partners.brand_logo when set), but we hit it as a separate URL
//        so the React Native Image cache can dedupe across screens and
//        survive re-mounts. Also covers the case where brand_logo is a
//        large base64 we'd rather not pass through redux.
//
//   3. logo.dev by domain
//        For partners who haven't uploaded a logo, fall back to the
//        company's domain via logo.dev — same service the Vault uses
//        (mobile/src/screens/VaultScreen.tsx#L181-223). Domain comes from
//        the partner row's email (preferred — the inbox tld is the most
//        reliable signal) or company_name when it parses as a domain.
//
//   4. Initials avatar
//        Two-letter monogram on a brand-primary background. Last resort
//        when nothing else resolves; matches the existing UserAvatar
//        pattern so the partner portal doesn't show a broken image icon.
//
// Each step uses Image's `onError` to advance to the next, so we never
// call APIs eagerly. The chain is purely render-driven.

import React, { useMemo, useState } from 'react';
import { Image, View, Text, StyleSheet } from 'react-native';
import config from '../config';
import { useTheme } from '../hooks/useTheme';

interface PartnerLike {
  account_id?: string | null;
  brand_logo?: string | null;
  company_name?: string | null;
  email?: string | null;
}

interface Props {
  partner?: PartnerLike | null;
  /** Edge length in dp; the logo renders square. */
  size?: number;
  /** Border radius. Defaults to size/4 for a soft-corner square. */
  radius?: number;
  /** Override base color of the initials fallback. Defaults to theme primary. */
  fallbackBackground?: string;
  style?: object;
}

type Stage = 'inline' | 'partners-api' | 'logo-dev' | 'initials';

// Pick the next domain candidate for logo.dev. The partner row's email
// domain is the most accurate signal (e.g. "lily@acmecare.com" → "acmecare.com");
// company_name is a poor signal because it's typically free-form text
// like "ACME Care" — only useful when it already parses as a domain
// (e.g. "acmecare.com").
function deriveDomain(partner: PartnerLike): string | null {
  if (partner.email && partner.email.includes('@')) {
    const domain = partner.email.split('@')[1]?.trim().toLowerCase();
    if (domain && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain)) return domain;
  }
  if (partner.company_name) {
    const cleaned = partner.company_name.trim().toLowerCase();
    // Only treat as a domain if it looks like one — otherwise logo.dev
    // returns 404 for arbitrary words.
    if (/^[a-z0-9.-]+\.[a-z]{2,}$/.test(cleaned)) return cleaned;
  }
  return null;
}

function buildPartnersApiUrl(accountId: string): string {
  // Mirror the resolved partners endpoint from config (handles dev /
  // staging / prod switch + EXPO_PUBLIC_PARTNERS_API_ENDPOINT override).
  // Cache-bust isn't needed — the binary endpoint sets short cache
  // headers and the Image component already keys on the URL itself.
  const base = config.partnersApi.endpoint.replace(/\/$/, '');
  return `${base}/logo/${encodeURIComponent(accountId)}`;
}

function initialsFor(partner: PartnerLike): string {
  const name = (partner.company_name || partner.email || '').trim();
  if (!name) return '?';
  // Strip leading non-alpha (e.g. "@user", "1company") then take first
  // letters of the first two whitespace-separated tokens. Matches the
  // existing UserAvatar shape on the project side.
  const tokens = name.replace(/^[^a-zA-Z0-9]+/, '').split(/[\s_-]+/).filter(Boolean);
  if (!tokens.length) return name[0]?.toUpperCase() || '?';
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[1][0]).toUpperCase();
}

export default function PartnerLogo({
  partner,
  size = 36,
  radius,
  fallbackBackground,
  style,
}: Props) {
  const theme = useTheme();
  const initialStage: Stage = useMemo(() => {
    if (!partner) return 'initials';
    if (partner.brand_logo) return 'inline';
    if (partner.account_id) return 'partners-api';
    if (deriveDomain(partner)) return 'logo-dev';
    return 'initials';
  }, [partner]);
  const [stage, setStage] = useState<Stage>(initialStage);

  // If partner identity changes (e.g. switching accounts), reset to the
  // first viable stage. useMemo above already handles this by
  // recomputing the `initialStage` when partner changes — but `setStage`
  // wouldn't otherwise pick up the new initial unless we trigger it.
  React.useEffect(() => {
    setStage(initialStage);
  }, [initialStage]);

  if (!partner) {
    return (
      <InitialsPlaceholder
        size={size}
        radius={radius}
        text="?"
        background={fallbackBackground || theme.colors.primary}
        textColor={theme.colors.primaryText}
        style={style}
      />
    );
  }

  const advance = (next: Stage) => () => setStage(next);

  if (stage === 'inline' && partner.brand_logo) {
    return (
      <Image
        source={{ uri: partner.brand_logo }}
        style={[
          { width: size, height: size, borderRadius: radius ?? Math.round(size * 0.2), backgroundColor: 'transparent' },
          style as any,
        ]}
        resizeMode="contain"
        onError={advance(partner.account_id ? 'partners-api' : (deriveDomain(partner) ? 'logo-dev' : 'initials'))}
      />
    );
  }

  if (stage === 'partners-api' && partner.account_id) {
    return (
      <Image
        source={{ uri: buildPartnersApiUrl(partner.account_id) }}
        style={[
          { width: size, height: size, borderRadius: radius ?? Math.round(size * 0.2), backgroundColor: 'transparent' },
          style as any,
        ]}
        resizeMode="contain"
        onError={advance(deriveDomain(partner) ? 'logo-dev' : 'initials')}
      />
    );
  }

  if (stage === 'logo-dev') {
    const domain = deriveDomain(partner);
    const token = config.logo?.devToken;
    if (domain && token) {
      const sizePx = Math.max(32, Math.round(size * 2)); // request a 2x asset for retina
      return (
        <Image
          source={{ uri: `https://img.logo.dev/${domain}?token=${token}&size=${sizePx}&format=png` }}
          style={[
            { width: size, height: size, borderRadius: radius ?? Math.round(size * 0.2), backgroundColor: 'transparent' },
            style as any,
          ]}
          resizeMode="contain"
          onError={advance('initials')}
        />
      );
    }
  }

  return (
    <InitialsPlaceholder
      size={size}
      radius={radius}
      text={initialsFor(partner)}
      background={fallbackBackground || theme.colors.primary}
      textColor={theme.colors.primaryText}
      style={style}
    />
  );
}

function InitialsPlaceholder({
  size,
  radius,
  text,
  background,
  textColor,
  style,
}: {
  size: number;
  radius?: number;
  text: string;
  background: string;
  textColor: string;
  style?: object;
}) {
  return (
    <View
      style={[
        styles.initials,
        {
          width: size,
          height: size,
          borderRadius: radius ?? Math.round(size * 0.2),
          backgroundColor: background,
        },
        style as any,
      ]}
    >
      <Text style={[styles.initialsText, { color: textColor, fontSize: Math.max(11, Math.round(size * 0.42)) }]}>
        {text}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  initials: { alignItems: 'center', justifyContent: 'center' },
  initialsText: { fontWeight: '700', letterSpacing: 0.5 },
});
